import { TextField as MUITextField, Typography } from "@material-ui/core";
import FormControl from "@material-ui/core/FormControl";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import FormGroup from "@material-ui/core/FormGroup";
import FormHelperText from "@material-ui/core/FormHelperText";
import InputLabel from "@material-ui/core/InputLabel";
import MenuItem from "@material-ui/core/MenuItem";
import Select from "@material-ui/core/Select";
import TableRow from "@material-ui/core/TableRow";
import { DatePicker } from "@material-ui/pickers";
import {
  DistrictField,
  ProvinceField,
  SubDistrictField,
  useAddress,
  ZipcodeField,
} from "components/request-document/components/Address";
import {
  Bagde,
  StyleCheckbox,
  StyledTableCell,
  useStyles,
} from "components/request-document/components/Commons";
import { getErrorMessage, isError } from "libs/utils/form";
import { get } from "lodash";
import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { canEditForm } from "redux/modules/authSlice";
import { selectMasterData } from "redux/modules/masterSlice";

const TextField = React.memo(MUITextField);

export default function PersonalInfoSection({ formik }) {
  const classes = useStyles();
  const dispatch = useDispatch();
  const canEdit = useSelector(canEditForm);
  const addressFieldOptions = useAddress({ formik });

  const masterData = useSelector(selectMasterData);
  const nationalityConfig = get(masterData, "nationalityConfig", []);

  return (
    <>
      <TableRow>
        <StyledTableCell colSpan={7}>
          <div className={classes.cellContent}>
            <Bagde number={1} />
            <div>
              <Typography>Full English name as in passport</Typography>
            </div>
          </div>
        </StyledTableCell>
      </TableRow>
      <TableRow>
        <StyledTableCell colSpan={3}>
          <div className={classes.cellContent}>
            <Bagde number={0} />
            <div className={classes.wrapField}>
              <InputLabel>First name</InputLabel>
              <TextField
                variant="outlined"
                className={classes.textField}
                name="personal.fistName"
                value={formik.values.personal.fistName}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={isError("personal.fistName", formik)}
                helperText={getErrorMessage("personal.fistName", formik)}
                disabled={!canEdit}
              />
            </div>
          </div>
        </StyledTableCell>
        <StyledTableCell colSpan={2}>
          <div className={classes.cellContent}>
            <div className={classes.wrapField}>
              <InputLabel>Middle name</InputLabel>
              <TextField
                variant="outlined"
                className={classes.textField}
                name="personal.middleName"
                value={formik.values.personal.middleName}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={isError("personal.middleName", formik)}
                helperText={getErrorMessage("personal.middleName", formik)}
                disabled={!canEdit}
              />
            </div>
          </div>
        </StyledTableCell>
        <StyledTableCell colSpan={2}>
          <div className={classes.cellContent}>
            <div className={classes.wrapField}>
              <InputLabel>Last name</InputLabel>
              <TextField
                variant="outlined"
                className={classes.textField}
                name="personal.lastName"
                value={formik.values.personal.lastName}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={isError("personal.lastName", formik)}
                helperText={getErrorMessage("personal.lastName", formik)}
                disabled={!canEdit}
              />
            </div>
          </div>
        </StyledTableCell>
      </TableRow>

      <TableRow>
        <StyledTableCell colSpan={3}>
          <div className={classes.cellContent}>
            <Bagde number={2} />
            <div className={classes.wrapField}>
              <InputLabel>
                Current Nationality / Nationality at birth, if different
              </InputLabel>
              <FormControl
                variant="outlined"
                className={classes.formControl}
                // style={{ width: 200 }}
                error={isError("personal.nationalityID", formik)}
              >
                <InputLabel>Select your nationality</InputLabel>
                <Select
                  label="Selected your nationality"
                  name="personal.nationalityID"
                  value={formik.values.personal.nationalityID}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  disabled={!canEdit}
                >
                  {nationalityConfig.map((nationality) => (
                    <MenuItem
                      value={nationality.nationalityID}
                      key={nationality.nationalityID}
                    >
                      {nationality.nationalityName}
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText
                  error={isError("personal.nationalityID", formik)}
                >
                  {getErrorMessage("personal.nationalityID", formik)}
                </FormHelperText>
              </FormControl>
            </div>
          </div>
        </StyledTableCell>
        <StyledTableCell colSpan={2}>
          <div className={classes.cellContent}>
            <Bagde number={3} />
            <div className={classes.wrapField}>
              <InputLabel>Date of birth (dd-mm-yyyy)</InputLabel>
              <DatePicker
                // style={{ width: 200 }}
                className={classes.formControl}
                inputVariant="outlined"
                disableFuture={false}
                openTo="year"
                format="DD-MM-YYYY"
                label="Date of birth"
                views={["year", "month", "date"]}
                name={`personal.birthDate`}
                value={get(formik.values, `personal.birthDate`, null) || null}
                onChange={(date) => {
                  formik.setFieldValue(
                    `personal.birthDate`,
                    date.format("YYYY-MM-DD")
                  );
                }}
                error={isError(`personal.birthDate`, formik)}
                helperText={getErrorMessage(`personal.birthDate`, formik)}
                disabled={!canEdit}
              />
            </div>
          </div>
        </StyledTableCell>
        <StyledTableCell colSpan={2}>
          <div className={classes.cellContent}>
            <Bagde number={4} />
            <div className={classes.wrapField}>
              <InputLabel style={{ wordBreak: "break-all" }}>
                Place of birth (city, province/state, country)
              </InputLabel>
              <TextField
                variant="outlined"
                className={classes.textField}
                name="personal.placeOfBirth"
                value={formik.values.personal.placeOfBirth}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={isError("personal.placeOfBirth", formik)}
                helperText={getErrorMessage("personal.placeOfBirth", formik)}
                disabled={!canEdit}
              />
            </div>
          </div>
        </StyledTableCell>
      </TableRow>

      <TableRow>
        <StyledTableCell colSpan={3}>
          <div className={classes.cellContent}>
            <Bagde number={5} />
            <div className={classes.wrapField}>
              <InputLabel>Gender</InputLabel>
              <FormGroup row>
                <FormControlLabel
                  control={
                    <StyleCheckbox
                      disabled={!canEdit}
                      name="personal.gender"
                      value="M"
                      checked={formik.values.personal.gender === "M"}
                      onClick={() => {
                        formik.setFieldValue("personal.gender", "M");
                      }}
                    />
                  }
                  label="Male"
                />
                <FormControlLabel
                  control={
                    <StyleCheckbox
                      disabled={!canEdit}
                      name="personal.gender"
                      value="F"
                      checked={formik.values.personal.gender === "F"}
                      onClick={() => {
                        formik.setFieldValue("personal.gender", "F");
                      }}
                    />
                  }
                  label="Female"
                />
              </FormGroup>
              <FormHelperText error={isError("personal.gender", formik)}>
                {getErrorMessage("personal.gender", formik)}
              </FormHelperText>
            </div>
          </div>
        </StyledTableCell>
        <StyledTableCell colSpan={4}>
          <div className={classes.cellContent}>
            <Bagde number={6} />
            <div className={classes.wrapField}>
              <InputLabel>Marital status</InputLabel>
              <FormGroup row>
                <FormControlLabel
                  control={
                    <StyleCheckbox
                      disabled={!canEdit}
                      name="Single"
                      value="S"
                      checked={formik.values.document.Marittal === "S"}
                      onClick={() => {
                        formik.setFieldValue("document.Marittal", "S");
                      }}
                    />
                  }
                  label="Single"
                />
                <FormControlLabel
                  control={
                    <StyleCheckbox
                      disabled={!canEdit}
                      name="Married"
                      value="M"
                      checked={formik.values.document.Marittal === "M"}
                      onClick={() => {
                        formik.setFieldValue("document.Marittal", "M");
                      }}
                    />
                  }
                  label="Married"
                />
                <FormControlLabel
                  control={
                    <StyleCheckbox
                      disabled={!canEdit}
                      name="Divorced"
                      value="D"
                      checked={formik.values.document.Marittal === "D"}
                      onClick={() => {
                        formik.setFieldValue("document.Marittal", "D");
                      }}
                    />
                  }
                  label="Divorced"
                />
                <FormControlLabel
                  control={
                    <StyleCheckbox
                      disabled={!canEdit}
                      name="Window"
                      value="W"
                      checked={formik.values.document.Marittal === "W"}
                      onClick={() => {
                        formik.setFieldValue("document.Marittal", "W");
                      }}
                    />
                  }
                  label="Windowed"
                />
              </FormGroup>
              <FormHelperText error={isError("document.Marittal", formik)}>
                {getErrorMessage("document.Marittal", formik)}
              </FormHelperText>
            </div>
          </div>
        </StyledTableCell>
      </TableRow>

      <TableRow>
        <StyledTableCell colSpan={3}>
          <div className={classes.cellContent}>
            <Bagde number={7} />
            <div className={classes.wrapField}>
              <InputLabel>Passport number</InputLabel>
              <TextField
                disabled={!canEdit}
                variant="outlined"
                className={classes.textField}
                name="personal.passportNumber"
                value={formik.values.personal.passportNumber}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={isError("personal.passportNumber", formik)}
                helperText={getErrorMessage("personal.passportNumber", formik)}
              />
            </div>
          </div>
        </StyledTableCell>
        <StyledTableCell colSpan={2}>
          <div className={classes.cellContent}>
            <Bagde number={8} />
            <div className={classes.wrapField}>
              <InputLabel>Date of issue (dd-mm-yyyy)</InputLabel>
              <DatePicker
                disabled={!canEdit}
                // style={{ width: 200 }}
                className={classes.formControl}
                inputVariant="outlined"
                disableFuture={false}
                openTo="year"
                format="DD-MM-YYYY"
                label="Date of issue"
                views={["year", "month", "date"]}
                name={`personal.dateofIssue`}
                value={get(formik.values, `personal.dateofIssue`, null) || null}
                onChange={(date) => {
                  formik.setFieldValue(
                    `personal.dateofIssue`,
                    date.format("YYYY-MM-DD")
                  );
                }}
                error={isError(`personal.dateofIssue`, formik)}
                helperText={getErrorMessage(`personal.dateofIssue`, formik)}
              />
            </div>
          </div>
        </StyledTableCell>
        <StyledTableCell colSpan={2}>
          <div className={classes.cellContent}>
            <Bagde number={9} />
            <div className={classes.wrapField}>
              <InputLabel>Valid until ( dd-mm-yyyy )</InputLabel>
              <DatePicker
                disabled={!canEdit}
                className={classes.formControl}
                // style={{ width: 200 }}
                inputVariant="outlined"
                disableFuture={false}
                openTo="year"
                format="DD-MM-YYYY"
                label="Valid until"
                views={["year", "month", "date"]}
                name={`personal.validuntil`}
                value={get(formik.values, `personal.validuntil`, null) || null}
                onChange={(date) => {
                  formik.setFieldValue(
                    `personal.validuntil`,
                    date.format("YYYY-MM-DD")
                  );
                }}
                error={isError(`personal.validuntil`, formik)}
                helperText={getErrorMessage(`personal.validuntil`, formik)}
              />
            </div>
          </div>
        </StyledTableCell>
      </TableRow>

      <TableRow>
        <StyledTableCell colSpan={3}>
          <div className={classes.cellContent}>
            <Bagde number={10} />
            <div className={classes.wrapField}>
              <InputLabel>Address in home country</InputLabel>
              <TextField
                disabled={!canEdit}
                variant="outlined"
                className={classes.textField}
                name="document.AddressInHome"
                value={formik.values.document.AddressInHome}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={isError("document.AddressInHome", formik)}
                helperText={getErrorMessage("document.AddressInHome", formik)}
              />
            </div>
          </div>
        </StyledTableCell>
        <StyledTableCell colSpan={4}>
          <div className={classes.cellContent}>
            <Bagde number={11} />
            <div className={classes.wrapField}>
              <FormGroup row style={{ marginTop: -10 }}>
                <FormControlLabel
                  control={
                    <StyleCheckbox
                      disabled={!canEdit}
                      name="document.HasAddressInThai"
                      checked={formik.values.document.HasAddressInThai}
                      onClick={() => {
                        const HasAddressInThai =
                          !formik.values.document.HasAddressInThai;

                        formik.setFieldValue(
                          "document.HasAddressInThai",
                          HasAddressInThai
                        );

                        if (!HasAddressInThai) {
                          formik.setFieldValue("document.AddressInThai", "");
                        }
                      }}
                    />
                  }
                  label="Current address in Thailand"
                />
              </FormGroup>
              <TextField
                variant="outlined"
                className={classes.textField}
                name="document.AddressInThai"
                value={formik.values.document.AddressInThai}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                disabled={!formik.values.document.HasAddressInThai || !canEdit}
                error={isError("document.AddressInThai", formik)}
                helperText={getErrorMessage("document.AddressInThai", formik)}
              />
            </div>
          </div>
        </StyledTableCell>
      </TableRow>

      <TableRow>
        <StyledTableCell colSpan={3}>
          <div className={classes.cellContent}>
            <Bagde number={12} />
            <div className={classes.wrapField}>
              <InputLabel>Phone number in Thailand</InputLabel>
              <TextField
                disabled={!canEdit}
                variant="outlined"
                className={classes.textField}
                name="document.PhoneNumber"
                inputProps={{ maxLength: 10 }}
                onKeyDown={(e) => {
                  const pattern = /[^0-9]/g;
                  const isNotAllow = e.key.length === 1 && pattern.test(e.key);

                  if (isNotAllow) {
                    e.preventDefault();
                  }
                }}
                value={formik.values.document.PhoneNumber}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={isError("document.PhoneNumber", formik)}
                helperText={getErrorMessage("document.PhoneNumber", formik)}
              />
            </div>
          </div>
        </StyledTableCell>
        <StyledTableCell colSpan={2}>
          <ProvinceField {...addressFieldOptions} />
        </StyledTableCell>
        <StyledTableCell colSpan={2}>
          <DistrictField {...addressFieldOptions} />
        </StyledTableCell>
      </TableRow>

      <TableRow>
        <StyledTableCell style={{ verticalAlign: "top" }} colSpan={3}>
          <div className={classes.cellContent}>
            <Bagde number={13} />
            <div className={classes.wrapField}>
              <InputLabel>E-mail address</InputLabel>
              <TextField
                disabled={!canEdit}
                variant="outlined"
                className={classes.textField}
                name="document.Email"
                value={formik.values.document.Email}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={isError("document.Email", formik)}
                helperText={getErrorMessage("document.Email", formik)}
              />
            </div>
          </div>
        </StyledTableCell>
        <StyledTableCell colSpan={2}>
          <SubDistrictField {...addressFieldOptions} />
        </StyledTableCell>
        <StyledTableCell colSpan={2}>
          <ZipcodeField {...addressFieldOptions} />
        </StyledTableCell>
      </TableRow>
    </>
  );
}
